import {
  ASSET_TYPE,
  CONTENT_SOURCE,
  DATE_PUBLISHED,
  LICENSE_TYPE,
  PERSONAL_FILTER,
  PRICE_RANGE,
  PURCHASE_FILTER,
  SORT_BY,
} from './constants'
import { SCOPE_PARAMS } from '../Shared/DesignerContent/constants'
import { styleFilterValue } from '../Shared/StyleContent/mock'

// Backend boolean filters expect the string '1' (see ItemsSearch::Options BOOLEAN_FIELDS /
// the legacy ListingPage filter components), never `true` or `1`.
const TRUTHY = '1'

// Store concepts that have NO matching backend search param on the shared listing JSON endpoint
// (ItemsSearch::Options). They are intentionally omitted so we never send unknown keys:
//   - quality (basic/premium/pro)        -> no ES filter
//   - sizeUnit/wholePrintSize/largestPrintSize/sizePreset -> no ES filter (needs backend work)
//   - aiUsageAllowed                      -> no param of its own; with specific licenses selected
//                                            it is expressed through the licenses slice (`_no_ai`
//                                            vs plain values); with "All Licenses" it expands to
//                                            AI_ALLOWED_LICENSES (see buildLicenses)
// TODO: revisit once backend support is added for the above.

// Quick Select price buckets map 1:1 to backend price_range tokens, except FREE. The listing
// endpoint has no trusted `free` raw param (ExtractItemsSearchOptions::TRUSTED_RAW_PARAMS); free
// is expressed as a 0-0 price range, which the backend resolves to free models
// (ItemsSearch::Options#setup_free_models_filter). more_than_100 is already the backend token.
const isFreeRange = (range) => range === PRICE_RANGE.FREE

// Free combined with other ranges must join the same price_range disjunction:
// ItemsSearch::Options#configure_price_filter ignores `price` whenever price_range is present,
// so sending Free as a separate price={0,0} alongside price_range would silently drop it.
export const FREE_RANGE_TOKEN = 'range_0_0'

const buildPriceParams = (state) => {
  const params = {}
  const hasFree = (state.priceRanges || []).some(isFreeRange)
  const ranges = (state.priceRanges || []).filter((range) => !isFreeRange(range))

  // Custom min/max maps to a `range_MIN_MAX` token, mirroring the legacy custom price range.
  // Min-only maps to `more_than_MIN`: the backend PRICE_RANGE_PARSER requires both bounds, so an
  // open-ended `range_MIN_` token would be silently dropped.
  const min = state.minPrice
  const max = state.maxPrice
  if (max !== '' && max !== undefined) {
    ranges.push(`range_${min || 0}_${max}`)
  } else if (min !== '' && min !== undefined) {
    ranges.push(`more_than_${min}`)
  }

  if (hasFree) {
    if (ranges.length > 0) {
      ranges.unshift(FREE_RANGE_TOKEN)
    } else {
      params.price = { from: 0, to: 0 }
    }
  }

  if (ranges.length > 0) {
    params.price_range = ranges
  }

  return params
}

// POLYGON_RANGE store values (lt_20k / range_20k_100k / gt_100k) already match backend tokens.
const buildPolygonParams = (state) => {
  const ranges = [...(state.polygonRanges || [])]

  // Min-only maps to `more_than_MIN` (POLYGONS_GT_PARSER_INT); an open-ended `range_MIN_` token
  // fails NEW_POLYGONS_RANGE_PARSER and would be silently dropped by the backend.
  if (state.maxPolygon !== undefined) {
    ranges.push(`range_${state.minPolygon || 0}_${state.maxPolygon}`)
  } else if (state.minPolygon !== undefined) {
    ranges.push(`more_than_${state.minPolygon}`)
  }

  return ranges.length > 0 ? { polygon_range: ranges } : {}
}

// Preset store values (last_7_days, ...) are the backend tokens (ItemsSearch::Options
// PUBLISHED_RANGE_PRESETS). A custom range is only sent once both calendar days are applied:
// the backend drops an open-ended `range_FROM_` token, and a lone bound has no UI state anyway.
const buildPublishedRangeParams = (state) => {
  const preset = state.datePublished

  if (preset === DATE_PUBLISHED.CUSTOM) {
    if (!state.minDatePublished || !state.maxDatePublished) return {}
    return { published_range: [`range_${state.minDatePublished}_${state.maxDatePublished}`] }
  }

  if (!preset || preset === DATE_PUBLISHED.ALL_TIME) return {}

  return { published_range: [preset] }
}

// License-type values match the ES `licenses` keyword field (Item::VALID_LICENSES) and ride the
// same `licenses[]` array as the restriction tokens; ItemsSearch::Options#configure_licenses_filter
// picks them out as a positive inclusion filter. `all` means "no license filter" and is dropped.
const LICENSE_VALUES = new Set(
  Object.values(LICENSE_TYPE).filter((value) => value !== LICENSE_TYPE.ALL)
)

// With "All Licenses" selected, the AI Usage toggle can't ride a specific license selection
// (setAiUsageAllowed leaves licenses untouched then), so it maps to the full AI-friendly license
// set instead. Mirrors Item::AI_FRIENDLY_LICENSES - editorial_royalty_free has no UI checkbox of
// its own but is an AI-friendly license and must stay included.
export const AI_ALLOWED_LICENSES = [
  LICENSE_TYPE.ROYALTY_FREE,
  LICENSE_TYPE.EDITORIAL,
  'editorial_royalty_free',
  LICENSE_TYPE.CUSTOM,
]

// advancedFilters, the hidden content-safety tokens, contentSource, asset-type, license-type and
// personal/purchase filters all funnel into the single backend `licenses[]` array.
const buildLicenses = (state) => {
  const licenses = [...(state.advancedFilters || []), ...(state.contentSafetyLicenses || [])]

  const selectedLicenses = (state.licenses || []).filter((license) => LICENSE_VALUES.has(license))
  if (selectedLicenses.length > 0) {
    licenses.push(...selectedLicenses)
  } else if (state.aiUsageAllowed) {
    // "All Licenses" + AI Usage Allowed: the toggle is the only restriction, so it must emit the
    // AI-friendly set itself - otherwise the params (and the fetch signature) never change.
    licenses.push(...AI_ALLOWED_LICENSES)
  }

  if (state.contentSource === CONTENT_SOURCE.AI_GENERATED) {
    licenses.push('ai_generated_only')
  } else if (state.contentSource === CONTENT_SOURCE.HUMAN_CREATED) {
    licenses.push('exclude_ai_generated')
  } else if (state.contentSource === CONTENT_SOURCE.ALL) {
    licenses.push('include_ai_generated')
  }

  // 3D-only excludes printable assets via the content-restriction token.
  if (state.assetType === ASSET_TYPE.THREE_D_ONLY) {
    licenses.push('exclude_3d_print')
  }

  if (state.personalFilter === PERSONAL_FILTER.WISH_LIST_ONLY) {
    licenses.push('wish_list_only')
  } else if (state.personalFilter === PERSONAL_FILTER.LIKED_MODELS_ONLY) {
    licenses.push('liked_models_only')
  }

  if (state.purchaseFilter === PURCHASE_FILTER.EXCLUDE_PURCHASED_MODELS) {
    licenses.push('hide_owned_models')
  } else if (state.purchaseFilter === PURCHASE_FILTER.PURCHASED_MODELS_ONLY) {
    licenses.push('owned_models')
  }

  return licenses
}

/**
 * Maps the SearchPage store state to the backend search params accepted by the shared listing
 * JSON endpoint (ExtractItemsSearchOptions::TRUSTED_RAW_PARAMS -> ItemsSearch::Options).
 *
 * Pure: no network, no store access. Defaults and unsupported concepts are omitted so the
 * resulting object only ever contains keys the backend understands.
 *
 * @param {Object} state - SearchPage store state (see Store/constants.js DEFAULT_STATE)
 * @returns {Object} Flat params object ready to pass as axios `params`
 */
const buildSearchParams = (state = {}) => {
  const params = {
    ...buildPriceParams(state),
    ...buildPolygonParams(state),
    ...buildPublishedRangeParams(state),
  }

  // Asset type: 3D-print-only sends `printable` (the trusted raw param that feeds the backend
  // `print_ready` filter, ItemsSearch::Options GENERAL_FILTERS_MAPPING); `print_ready` itself is
  // not trusted. 3D-only is handled via licenses below.
  if (state.assetType === ASSET_TYPE.THREE_D_PRINT_ONLY) {
    params.printable = TRUTHY
  }

  if ((state.fileTypes || []).length > 0) {
    params.file_types = state.fileTypes
  }

  if (state.onSale) {
    params.sale_off = TRUTHY
  }

  // `subscribed` is the buyer-facing catalog filter (opted-in models + tier-band gating in
  // SearchExecutorHelper); `eligible_for_subscription` is the seller-facing "eligible but not
  // opted in" filter and must NOT be sent here.
  if (state.withSubscription) {
    params.subscribed = TRUTHY
  }

  // The multi-author filter takes usernames, not ids, on the `author[]` param - which is what the
  // designers slice already stores. Axios' default serializer expands the array to
  // `author[]=a&author[]=b`; a comma-joined `author=a,b` is not supported and 404s.
  if ((state.designers || []).length > 0) {
    params.author = state.designers
  }

  // A scope the user ticked whole travels as its own flag rather than as its members' usernames.
  // The backend unions these with `author[]` into one user_ids terms filter, so the two compose.
  (state.designerScopes || []).forEach((scope) => {
    const param = SCOPE_PARAMS[scope]
    if (param) {
      params[param] = '1'
    }
  })

  const licenses = buildLicenses(state)
  if (licenses.length > 0) {
    params.licenses = licenses
  }

  // `at_style` is an array of lowercase ES keyword tokens (array = any-of); the store keeps the
  // display labels, so map through styleFilterValue ("Sci-Fi" -> "sci-fi").
  if ((state.styles || []).length > 0) {
    params.at_style = state.styles.map(styleFilterValue)
  }

  if ((state.tags || []).length > 0) {
    params.suggested_keywords = state.tags.join(',')
  }

  // "Search within results" rides its own param: SearchExecutorHelper#effective_keywords joins it
  // onto `keywords` before normalisation, so the default AND operator narrows the current set. It
  // must NOT be merged into `keywords` here - that param is re-read from the URL on every fetch
  // (see actions/results.js) and the backend echoes it back into the canonical URL, so a merged
  // term would be absorbed into the base query, grow on each refetch, and never be clearable.
  const withinKeywords = (state.keyword || '').trim()
  if (withinKeywords !== '') {
    params.within_keywords = withinKeywords
  }

  if (state.animated) {
    params.animated = TRUTHY
  }
  if (state.pbr) {
    params.pbr = TRUTHY
  }
  if (state.rigged) {
    params.rigged = TRUTHY
  }
  if (state.gameReady) {
    params.game_ready = TRUTHY
  }
  if (state.withRenders) {
    params.with_renders = TRUTHY
  }
  if (state.withMakes) {
    params.with_makes = TRUTHY
  }
  if (state.threeDPrintVerified) {
    params.cgt_verified = TRUTHY
  }

  if (state.sortBy && state.sortBy !== SORT_BY.BEST_MATCH) {
    params.sort_by = state.sortBy
  }

  return params
}

export default buildSearchParams
